"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const silica_1 = require("@opendoor/silica");
const materialThemeConfig_1 = __importDefault(require("./materialThemeConfig"));
// The design team is working on a new set of internal tools to replace Admin. This
// effort involves setting new standards that vary from the old Mosaic Material theme.
// In this V2 theme config, we'll largely inherit from V1, but add overrides. As this
// diverges from the legacy config, or if this V2 is adopted across all tools that use
// Mosaic, we can merge the two rather than inheriting.
exports.default = Object.assign(Object.assign({}, materialThemeConfig_1.default), { typography: {
        fontFamily: "'Graphik', sans-serif",
        h1: {
            fontSize: '2rem',
            fontWeight: 600,
        },
        h2: {
            fontSize: '1.75rem',
            fontWeight: 600,
        },
        h3: {
            fontSize: '1.5rem',
            fontWeight: 600,
        },
        h4: {
            fontSize: '1.25rem',
            fontWeight: 600,
        },
        h5: {
            fontSize: '1rem',
            fontWeight: 600,
        },
        h6: {
            fontSize: '0.875rem',
            fontWeight: 600,
        },
        subtitle1: {
            fontSize: '1rem',
            fontWeight: 500,
        },
        subtitle2: {
            fontSize: '1rem',
            fontWeight: 400,
        },
        body1: {
            fontSize: '0.875rem',
        },
        body2: {
            fontSize: '0.75rem',
        },
        caption: {
            fontSize: '0.6875rem',
        },
    }, overrides: Object.assign(Object.assign({}, materialThemeConfig_1.default.overrides), { MuiButton: {
            root: {
                color: silica_1.colors.gray5,
                textTransform: 'initial',
                lineHeight: '24px',
                fontWeight: 600,
                padding: `${silica_1.spacingSizes['3']} ${silica_1.spacingSizes['5']}`,
                minWidth: 'unset',
                // remove the transition as it is out of sync with the icon transition
                transition: 'none',
                '&:hover': {
                    color: silica_1.colors.blue6,
                },
                // svg colors
                '& .MuiSvgIcon-root': {
                    stroke: 'transparent',
                    fill: 'currentColor',
                    // remove the svg transition as it is out of sync with the button
                    transition: 'none',
                },
                '&:hover .MuiSvgIcon-root': {
                    fill: 'currentColor',
                },
                '&.Mui-disabled .MuiSvgIcon-root': {
                    fill: 'currentColor',
                },
            },
            containedPrimary: {
                '& .MuiSvgIcon-root': {
                    fill: silica_1.colors.white,
                },
            },
            containedSecondary: {
                color: silica_1.colors.black,
                backgroundColor: silica_1.colors.gray2,
                '& .MuiSvgIcon-root': {
                    fill: silica_1.colors.black,
                },
                '&:hover': {
                    color: silica_1.colors.black,
                    backgroundColor: silica_1.colors.gray3,
                },
                '&:hover .MuiSvgIcon-root': {
                    fill: silica_1.colors.black,
                },
            },
            sizeLarge: {
                padding: `${silica_1.spacingSizes['4']} ${silica_1.spacingSizes['6']}`,
            },
            text: {
                // Ensure text Buttons have the same sizing as other variants
                padding: undefined,
                '&:hover': {
                    backgroundColor: silica_1.colors.blue3,
                    '& .MuiSvgIcon-root': {
                        fill: silica_1.colors.blue6,
                    },
                },
            },
            textPrimary: {
                color: silica_1.colors.blue5,
                '&:hover': {
                    backgroundColor: silica_1.colors.blue3,
                },
                '& .MuiSvgIcon-root': {
                    fill: silica_1.colors.blue5,
                },
            },
            textSecondary: {
                color: silica_1.colors.gray4,
                '&:hover': {
                    backgroundColor: silica_1.colors.gray2,
                },
            },
            outlined: {
                // Match the padding of MuiButton-root while accounting for the 1px border of outlined buttons
                padding: `calc(${silica_1.spacingSizes['3']} - 1px) calc(${silica_1.spacingSizes['5']} - 1px)`,
                borderColor: silica_1.colors.gray4,
                '& .MuiSvgIcon-root': {
                    fill: silica_1.colors.gray5,
                },
                // All outlined buttons use this hover effect
                '&:hover': {
                    color: silica_1.colors.blue6,
                    backgroundColor: 'transparent',
                    borderColor: silica_1.colors.blue6,
                    '& .MuiSvgIcon-root': {
                        fill: silica_1.colors.blue6,
                    },
                },
            },
            outlinedSizeLarge: {
                '&$sizeLarge': {
                    // for specificity, to override $sizeLarge
                    padding: `calc(${silica_1.spacingSizes['4']} - 1px) calc(${silica_1.spacingSizes['6']})`,
                },
            },
            outlinedSizeSmall: {
                '&$sizeSmall': {
                    // for specificity, to override $sizeSmall
                    padding: `calc(${silica_1.spacingSizes['2']} - 1px) ${silica_1.spacingSizes['3']}`,
                },
            },
            startIcon: {
                '&$iconSizeSmall': {
                    marginLeft: 0,
                    marginRight: silica_1.spacingSizes['2'],
                },
            },
            endIcon: {
                '&$iconSizeSmall': {
                    marginLeft: silica_1.spacingSizes['2'],
                    marginRight: 0,
                },
            },
            // color-related
            contained: {
                color: silica_1.colors.gray6,
                backgroundColor: silica_1.colors.gray2,
                '&:hover': {
                    color: silica_1.colors.white,
                    backgroundColor: silica_1.colors.blue5,
                },
            },
            outlinedPrimary: {
                color: silica_1.colors.blue4,
                borderColor: silica_1.colors.blue4,
                '& .MuiSvgIcon-root': {
                    fill: silica_1.colors.blue4,
                },
                '&:hover': {
                    backgroundColor: undefined,
                    borderColor: silica_1.colors.blue6,
                },
            },
            outlinedSecondary: {
                borderColor: silica_1.colors.gray4,
                color: silica_1.colors.gray4,
                '& .MuiSvgIcon-root': {
                    fill: silica_1.colors.gray4,
                },
                '&:hover': {
                    borderColor: silica_1.colors.gray6,
                    color: silica_1.colors.gray6,
                    backgroundColor: undefined,
                    border: undefined,
                    borderWidth: '1px',
                    borderStyle: 'solid',
                },
            },
        } }) });
